/**
 * Portal-dropdown lifecycle helper for selection UIs (searchable-multi engine,
 * chips engine, async multiselect filter driver).
 *
 * Encapsulates: position-fixed positioning from anchor rect, body-append on
 * `open()`, outside-click close (via document mousedown), scroll/resize
 * reposition, and the `suppressNextOutsideClose()` race fix needed when the
 * caller rebuilds DOM that contained a mousedown target.
 *
 * Uses `anchor.ownerDocument` and its defaultView (not bare `document`/
 * `window`) so detached docs, iframes, and jsdom test setups work.
 *
 * @param {{
 *   anchor: HTMLElement,
 *   panel: HTMLElement,
 *   onClose: () => void,
 * }} cfg
 * @returns {{
 *   open(): void,
 *   close(): void,
 *   reposition(): void,
 *   suppressNextOutsideClose(): void,
 *   destroy(): void,
 * }}
 */
export function createPortalDropdown(cfg) {
  const { anchor, panel, onClose } = cfg;
  const ownerDocument = anchor.ownerDocument;
  const ownerWindow = ownerDocument.defaultView;

  let isOpen = false;
  let destroyed = false;
  let skipNextOutsideClose = false;

  function position() {
    const rect = anchor.getBoundingClientRect();
    panel.style.position = 'fixed';
    panel.style.left = `${rect.left}px`;
    panel.style.top = `${rect.bottom}px`;
    panel.style.minWidth = `${rect.width}px`;
    panel.style.zIndex = '9999';
  }

  function handleDocumentMousedown(/** @type {MouseEvent} */ e) {
    if (destroyed || !isOpen) return;
    if (skipNextOutsideClose) {
      skipNextOutsideClose = false;
      return;
    }
    const target = /** @type {Node} */ (e.target);
    // Inside anchor or panel → not an "outside" click.
    if (anchor.contains(target) || panel.contains(target)) return;
    onClose();
  }

  function handleReposition() {
    if (isOpen) position();
  }

  ownerDocument.addEventListener('mousedown', handleDocumentMousedown);
  if (ownerWindow) {
    ownerWindow.addEventListener('scroll', handleReposition, { passive: true, capture: true });
    ownerWindow.addEventListener('resize', handleReposition, { passive: true });
  }

  return {
    open() {
      if (destroyed || isOpen) return;
      isOpen = true;
      if (!panel.isConnected) ownerDocument.body.appendChild(panel);
      panel.style.display = '';
      position();
    },
    close() {
      if (!isOpen) return;
      isOpen = false;
      panel.style.display = 'none';
    },
    reposition() {
      if (isOpen) position();
    },
    suppressNextOutsideClose() {
      skipNextOutsideClose = true;
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      isOpen = false;
      ownerDocument.removeEventListener('mousedown', handleDocumentMousedown);
      if (ownerWindow) {
        ownerWindow.removeEventListener('scroll', handleReposition, /** @type {any} */ ({ capture: true }));
        ownerWindow.removeEventListener('resize', handleReposition);
      }
      if (panel.parentNode) panel.parentNode.removeChild(panel);
    },
  };
}
